import React, { useState } from 'react';
import AvLegend, { ISeries } from '@components/AvLegend';
import { ChartClickInteraction } from '@components/Widgets/types';
import { AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, SxProps, useTheme } from '@mui/material';
import { getPercent } from '@utils/utils';
import CustomTooltip from '../../../../../components/Widgets/CustomTooltip';
import { useFormattingRules } from '../../../../FormattingRules/hooks';
import { getColorFromFormattingConditions, getFormattingRuleById } from '../../../../FormattingRules/utils';
import { OrientationType } from '../../../constants';
import { useGetDisplayName } from '../../../hooks/useGetDisplayName';
import { CommonWidgetProps, tileTitleSX, TileTypeWidget } from '../../../types';
import { ProgressClickthroughState } from '../../Clickthrough/utils';

export interface ProgressWidgetProps extends CommonWidgetProps {
  widget: TileTypeWidget;
  metricsValues: number[];
  orientation?: OrientationType;
  titleTextSx?: SxProps;
  barWidth?: number | string;
  clickInteractions?: ChartClickInteraction[];
  applyFormattingRuleOnComplement?: boolean;
  hideLegends?: boolean;
}

const verticalSx = {
  px: 1,
  flexShrink: 0,
  ...flex.col,
  height: '100%',
  maxHeight: 300,
};
const horizontalSx = {
  ...flex.justifyEndCenter,
  height: '100%',
};

const boxShadowSx = '0px 2px 20px rgba(18, 18, 27, 0.15)';

const commonSx = {
  transition: 'box-shadow 0.1s ease-in-out',
  height: '12px',
  '&:hover': {
    boxShadow: boxShadowSx,
  },
};

const textSx = { color: theme => theme.palette.colors.neutrals[550], fontSize: '12px', fontWeight: 400, lineHeight: '18px' };
const numberSx = { color: theme => theme.palette.colors.neutrals[700], fontSize: '14px', fontWeight: 600, lineHeight: '20px' };

const ProgressTileWidget = ({
  widget,
  metricsValues,
  orientation = OrientationType.Vertical,
  titleTextSx = tileTitleSX,
  barWidth = '100%',
  clickInteractions,
  selected,
  applyFormattingRuleOnComplement = false,
  hideLegends = true,
}: ProgressWidgetProps) => {
  const theme = useTheme();
  const [metric0, metric1, metric2] = metricsValues;
  const maxWidth = orientation === OrientationType.Horizontal ? 700 : 900;
  const {
    select: {
      metrics: [{ name: metricName }],
    },
    projectionId: { name: projName },
  } = widget.requests[0];

  const activeState: ProgressClickthroughState = selected?.[0]?.activeState;

  const getDisplayName = useGetDisplayName(projName);
  const { data: formattingRules = [] } = useFormattingRules();
  const { completionDisplayName, hideCompletionDescription, hidePercentage, complementColor, legendsNames } = widget.definition.custom;

  const shouldPaintComplement = metric0 <= 0.5 && applyFormattingRuleOnComplement;
  const barBgColor = complementColor || theme.palette.colors.neutrals[shouldPaintComplement ? 100 : 350];

  const isEnabledClickthrough = clickInteractions?.some(({ isEnabledForCell }) => isEnabledForCell({ dataKey: metricName }));

  const onClick = ({ percentageComplement }: { percentageComplement: boolean }) => {
    if (isEnabledClickthrough && clickInteractions?.length) {
      clickInteractions[0].onClick({ percentageComplement });
    }
  };

  const formattingRuleColor = theme => {
    const formattingRuleById = getFormattingRuleById(widget.definition.custom?.formattingRuleId, formattingRules);
    return getColorFromFormattingConditions({
      value: metric0,
      formattingRule: formattingRuleById,
      defaultColor: theme.palette.colors.primary[500],
    });
  };

  const series: ISeries[] = legendsNames
    ? [
        { name: legendsNames?.numerator, color: formattingRuleColor(theme) },
        { name: legendsNames?.denominator, color: barBgColor },
      ]
    : [];
  const [isInnerHovered, setIsInnerHovered] = useState<boolean>(false);
  const FILLED_PART_SX: SxProps = {
    width: `${metric0}%`,
    background: theme => formattingRuleColor(theme),
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
    ...(activeState === ProgressClickthroughState.NotPercentageComplement && {
      boxShadow: boxShadowSx,
    }),
    ...(activeState === ProgressClickthroughState.PercentageComplement && {
      opacity: 0.4,
    }),
    ...(metric0 === 100 ? { borderRadius: 20 } : {}),
    ...commonSx,
  };
  const OUTER_BOX_SX: SxProps = {
    maxWidth,
    width: '100%',
    borderRadius: 20,
    background: barBgColor,
    cursor: 'pointer',
    overflow: activeState === ProgressClickthroughState.NotPercentageComplement || isInnerHovered ? 'visible' : 'hidden',
    ...flex.row,
    ...(shouldPaintComplement && {
      background: theme => formattingRuleColor(theme),
      opacity: 0.18,
    }),
    ...(activeState === ProgressClickthroughState.PercentageComplement && {
      boxShadow: boxShadowSx,
    }),
    ...(!isInnerHovered && commonSx),
  };

  return (
    <Box sx={orientation === OrientationType.Vertical ? verticalSx : horizontalSx}>
      {!hideLegends && series.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {' '}
          <AvLegend series={series} isHorizontal />{' '}
        </Box>
      )}
      {!hidePercentage && <Box sx={titleTextSx}>{getPercent(metric0)}</Box>}
      <Box
        sx={{
          height: '100%',
          width: barWidth,
          maxWidth,
          maxHeight: 300,
          mb: hideCompletionDescription ? 0 : orientation === OrientationType.Vertical ? 6 : 2.5,
          paddingLeft: orientation === OrientationType.Horizontal ? 2.5 : 0,
          ...(orientation === OrientationType.Horizontal ? flex.colEnd : flex.colEndStart),
        }}>
        <Box
          sx={{
            maxWidth,
            width: barWidth,
          }}
          className="draggableCancel">
          <AvTooltip
            variant="message"
            muiProps={{
              placement: 'right',
              componentsProps: {
                popper: { sx: { '& .MuiTooltip-tooltip': { p: 0 }, ...(isInnerHovered ? { opacity: 0 } : {}) } },
              },
            }}
            title={
              <CustomTooltip
                payload={[
                  {
                    payload: { [legendsNames?.denominator || metricName]: getPercent(100 - metric0), fill: barBgColor },
                    dataKey: legendsNames?.denominator || metricName,
                  },
                ]}
                showTitle={false}
                showContentLegend
                clickInteractions={clickInteractions}
                metricFormatter={() => legendsNames?.denominator || getDisplayName?.(metricName)?.toString() || metricName}
                metricKey={legendsNames?.denominator || metricName}
                sx={{ ml: 0 }}
              />
            }>
            <Box sx={OUTER_BOX_SX} onClick={() => onClick({ percentageComplement: true })} role="progressbar">
              <AvTooltip
                variant="message"
                muiProps={{
                  placement: 'right',
                  componentsProps: {
                    popper: { sx: { '& .MuiTooltip-tooltip': { p: 0 } } },
                  },
                }}
                title={
                  <CustomTooltip
                    payload={[
                      {
                        payload: { [legendsNames?.numerator || metricName]: getPercent(metric0), fill: formattingRuleColor(theme) },
                        dataKey: legendsNames?.numerator || metricName,
                      },
                    ]}
                    showTitle={false}
                    showContentLegend
                    clickInteractions={clickInteractions}
                    metricFormatter={() => legendsNames?.numerator || getDisplayName?.(metricName)?.toString() || metricName}
                    metricKey={legendsNames?.numerator || metricName}
                    sx={{ ml: 0 }}
                  />
                }>
                <Box
                  onMouseEnter={() => setIsInnerHovered(true)}
                  onMouseLeave={() => setIsInnerHovered(false)}
                  onClick={e => {
                    e.stopPropagation(); // Prevent outer tooltip click event
                    onClick({ percentageComplement: false });
                  }}
                  sx={FILLED_PART_SX}
                />
              </AvTooltip>
            </Box>
          </AvTooltip>
        </Box>
        {!hideCompletionDescription && (
          <Box sx={{ ...flex.justifyBetweenCenter, width: '100%', maxWidth: 900, mt: 0.5, px: 0.5 }}>
            <Box sx={{ ...flex.justifyBetweenEnd, gap: 0.5 }}>
              <Box sx={numberSx}> {metric1}</Box>
              <Box sx={textSx}>{completionDisplayName || ''} </Box>
            </Box>
            <Box sx={{ ...flex.justifyBetweenEnd, gap: 0.5 }}>
              <Box sx={textSx}> Out of</Box>
              <Box sx={numberSx}> {metric2} </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default ProgressTileWidget;
